import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { SWRConfig } from "swr";

import { ConfigContext } from "@/hooks/useConfigContext";
import client from "@/lib/client";
import { inter } from "@/lib/fonts";
import I18n, { defaultLanguage, languages } from "@/lib/i18n";

import "@/lib/tailwind.css";

import type { RequestDocument, Variables } from "graphql-request";
import type { AppProps } from "next/app";

import type { Config } from "@/hooks/useConfigContext";

const fetcher = (
  params: [RequestDocument, Variables, AbortController] | RequestDocument,
) => {
  const [document, variables, abortController] = Array.isArray(params)
    ? params
    : [params];

  if ((document as string)?.startsWith("http")) {
    return fetch(document as string, { signal: abortController?.signal }).then(
      (res) => {
        return res.json();
      },
    );
  }
  //@ts-expect-error - There is a conflict between AbortSignal types
  return client.request<unknown>({
    document: document,
    signal: abortController?.signal,
    variables,
  });
};

export default function App({
  Component,
  pageProps,
}: AppProps<{ lang?: string; tenant?: string }>) {
  const [config, setConfig] = useState<Config>();
  const router = useRouter();

  const locale = useMemo(() => {
    const urlLocale = (router.query.lang as string) || pageProps?.lang;
    if (urlLocale && languages.includes(urlLocale)) {
      return urlLocale;
    }
    return defaultLanguage;
  }, [router.query.lang, pageProps?.lang]);

  const onError = useCallback(
    (error: { response?: { status?: number } }) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        void router.push(`/${locale}/login`);
      }
    },
    [router, locale],
  );

  return (
    <I18n locale={locale}>
      <div className={inter.className}>
        <SWRConfig value={{ fetcher, onError }}>
          <ConfigContext.Provider value={{ config, setConfig }}>
            <Component {...pageProps} />
          </ConfigContext.Provider>
        </SWRConfig>
      </div>
    </I18n>
  );
}
